import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatPrice, formatApiErrorDetail } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const inputCls = "w-full border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

export default function CheckoutPage() {
  const { items, subtotal, vat, total, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", phone: user?.phone || "",
    company_name: user?.company_name || "", reg_number: user?.reg_number || "", vat_number: user?.vat_number || "",
    method: "courier", address: "", city: "", zip: "", notes: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-heading text-3xl font-black uppercase tracking-tighter">Grozs ir tukšs</h1>
        <Link to="/katalogs" className="btn-industrial mt-6 inline-block bg-[#2E86D1] px-6 py-3 text-sm font-semibold text-white hover:bg-[#1F6DB2]">Doties uz katalogu</Link>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) { toast.error("Lūdzu, aizpildiet obligātos laukus"); return; }
    if (form.method === "courier" && !form.address) { toast.error("Lūdzu, norādiet piegādes adresi"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ sku: i.sku, qty: i.qty })),
        customer: { name: form.name, email: form.email, phone: form.phone, company_name: form.company_name || null, reg_number: form.reg_number || null, vat_number: form.vat_number || null },
        delivery: { method: form.method, address: form.address || null, city: form.city || null, zip: form.zip || null, notes: form.notes || null },
      });
      clear();
      navigate(`/pasutijums/${data.number}`, { state: { order: data } });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Pasūtījuma noformēšana</h1>
      <p className="mt-3 border border-blue-200 bg-blue-50 p-3 text-sm text-slate-600" data-testid="checkout-demo-notice">
        Demo versija — pasūtījums netiks nosūtīts. Tas tiks saglabāts tikai šajā pārlūkā, lai varētu izmēģināt statusu un konta vēsturi.
      </p>
      <form onSubmit={submit} className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-8 items-start">
        <div className="space-y-8">
          <section className="rounded-2xl border border-border p-6">
            <h2 className="font-heading text-lg font-bold uppercase">1. Kontaktinformācija</h2>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <input data-testid="checkout-name" placeholder="Vārds, uzvārds *" value={form.name} onChange={set("name")} className={inputCls} />
              <input data-testid="checkout-email" type="email" placeholder="E-pasts *" value={form.email} onChange={set("email")} className={inputCls} />
              <input data-testid="checkout-phone" placeholder="Tālrunis *" value={form.phone} onChange={set("phone")} className={inputCls} />
            </div>
          </section>

          <section className="rounded-2xl border border-border p-6">
            <h2 className="font-heading text-lg font-bold uppercase">2. Uzņēmuma dati <span className="text-xs font-normal text-slate-500 normal-case">(rēķinam, ja pērkat kā juridiska persona)</span></h2>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <input data-testid="checkout-company" placeholder="Uzņēmuma nosaukums" value={form.company_name} onChange={set("company_name")} className={inputCls} />
              <input data-testid="checkout-regnr" placeholder="Reģ. numurs" value={form.reg_number} onChange={set("reg_number")} className={inputCls} />
              <input data-testid="checkout-vatnr" placeholder="PVN numurs" value={form.vat_number} onChange={set("vat_number")} className={inputCls} />
            </div>
          </section>

          <section className="rounded-2xl border border-border p-6">
            <h2 className="font-heading text-lg font-bold uppercase">3. Piegāde</h2>
            <div className="mt-4 space-y-3">
              <label className="flex items-center gap-3 border border-border p-3 cursor-pointer hover:border-[#2E86D1]">
                <input data-testid="delivery-courier" type="radio" name="method" checked={form.method === "courier"} onChange={() => setForm({ ...form, method: "courier" })} className="accent-[#2E86D1]" />
                <div><p className="text-sm font-bold">Piegāde ar kurjeru</p><p className="text-xs text-slate-500">1–2 darba dienas visā Latvijā. Mucas piegādājam ar autoiekrāvēju.</p></div>
              </label>
              <label className="flex items-center gap-3 border border-border p-3 cursor-pointer hover:border-[#2E86D1]">
                <input data-testid="delivery-pickup" type="radio" name="method" checked={form.method === "pickup"} onChange={() => setForm({ ...form, method: "pickup" })} className="accent-[#2E86D1]" />
                <div><p className="text-sm font-bold">Saņemšana noliktavā Rīgā</p><p className="text-xs text-slate-500">Bez maksas, darba dienās 8:00–18:00</p></div>
              </label>
              {form.method === "courier" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                  <input data-testid="checkout-address" placeholder="Adrese *" value={form.address} onChange={set("address")} className={`${inputCls} md:col-span-2`} />
                  <input data-testid="checkout-city" placeholder="Pilsēta / novads" value={form.city} onChange={set("city")} className={inputCls} />
                </div>
              )}
              <textarea data-testid="checkout-notes" placeholder="Piezīmes pasūtījumam" value={form.notes} onChange={set("notes")} rows={2} className={inputCls} />
            </div>
          </section>

          <section className="rounded-2xl border border-border p-6">
            <h2 className="font-heading text-lg font-bold uppercase">4. Apmaksa</h2>
            <div className="mt-4 border border-slate-300 bg-[#F4F5F7] p-4">
              <p className="text-sm font-bold">Rēķins (pēcapmaksa)</p>
              <p className="mt-1 text-xs text-slate-600">Pēc pasūtījuma apstiprināšanas nosūtīsim rēķinu uz jūsu e-pastu. Preces tiek rezervētas uzreiz.</p>
            </div>
          </section>
        </div>

        <div className="rounded-2xl border border-slate-300 p-6 sticky top-24">
          <h2 className="font-heading text-lg font-bold uppercase">Jūsu pasūtījums</h2>
          <div className="mt-4 space-y-2 max-h-56 overflow-auto">
            {items.map((i) => (
              <div key={i.sku} className="flex justify-between gap-2 text-xs border-b border-border pb-2">
                <span className="line-clamp-1">{i.qty}× {i.name}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>Bez PVN</span><span className="font-semibold">{formatPrice(subtotal)}</span></div>
            <div className="flex justify-between"><span>PVN 21%</span><span>{formatPrice(vat)}</span></div>
            {user?.discount_pct > 0 && <p className="text-xs text-[#16A34A] font-semibold">Jūsu B2B atlaide {user.discount_pct}% tiks piemērota rēķinā</p>}
            <div className="flex justify-between border-t border-slate-300 pt-3 text-base font-black font-heading"><span>KOPĀ</span><span>{formatPrice(total)}</span></div>
          </div>
          <button data-testid="submit-order-btn" type="submit" disabled={submitting}
            className="btn-industrial mt-6 w-full bg-[#2E86D1] py-3.5 text-sm font-semibold tracking-wide text-white hover:bg-[#1F6DB2] disabled:opacity-50">
            {submitting ? "Noformē..." : "Apstiprināt pasūtījumu"}
          </button>
        </div>
      </form>
    </div>
  );
}
